/*
* === MaintenancePage.js ===
* Features:
* - User friendly "Under Construction" message
* - Optional Countdown Timer
* - Login Bypass link for Admins
*/
import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button, Paper } from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { Link } from 'react-router-dom';

const MaintenancePage = ({ endTime, showCountdown }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        if (!showCountdown || !endTime) return;

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const distance = new Date(endTime).getTime() - now;

            if (distance < 0) {
                setTimeLeft("Maintenance should be complete soon.");
                clearInterval(interval);
                return;
            }

            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
        }, 1000);

        return () => clearInterval(interval);
    }, [endTime, showCountdown]);

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'background.default',
            color: 'text.primary'
        }}>
            <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
                <EngineeringIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                    We'll be right back.
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph>
                    The system is currently undergoing scheduled maintenance to improve your experience.
                </Typography>
                
                {showCountdown && timeLeft && (
                    <Paper elevation={0} sx={{ p: 2, my: 4, bgcolor: 'action.hover', borderRadius: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Estimated time remaining:
                        </Typography>
                        <Typography variant="h4" fontFamily="monospace" fontWeight="bold" color="primary">
                            {timeLeft}
                        </Typography>
                    </Paper>
                )}

                <Box sx={{ mt: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Are you the System Administrator?
                    </Typography>
                    <Button component={Link} to="/login" size="small" color="inherit">
                        Admin Login
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default MaintenancePage;